import React, { useCallback, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import { CircularProgress, Fade, IconButton, Menu, MenuItem } from "@mui/material";
import { NullifyUnfundedCommitment } from "components/deal";
import { TooltipDiv } from "components/tooltips/TooltipDiv";
import { useModals } from "hooks";
import { startCase } from "lodash";
import {
  getShownCalculatedFields,
  IApiServiceError,
  useDealIdSelector,
  useReportsActions,
  useReportsQueriesSelector,
  useReportsTableSelector
} from "store";
import { DealTableRow, RoutesKeys } from "store/types";

import { Routes } from "../../../constants/routes";

type DownloadFile = {
  type?: string;
  name?: string;
  data: string;
};

const routesForServerExports = [Routes.snapshot, Routes.dealReport];

type ExportTableNames = Partial<Record<RoutesKeys, string>>;

const exportTableNames: ExportTableNames = {
  dealReport: "Export Deal Measures Table",
  snapshot: "Export Portfolio Analysis Table"
};

export const ContextMenuReports: React.FC = () => {
  const dealId = useDealIdSelector();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [anchorEl, setAnchor] = useState(null);
  const tableData = useReportsTableSelector();
  const calcFields = getShownCalculatedFields();
  const { openIRRGraphModal } = useModals();
  const {
    filters,
    dateRange,
    measures,
    characteristics,
    currency,
    exchangeMethod,
    calculatedFields,
    filteredPortfolio,
    secondaryDateRange,
    groupBy,
    rowBy,
    originalCurrency,
    dealTableRowBy,
    dealTableGroupBy,
    dealTableColumnBy,
    use_accounting_date,
    reportTableColumns,
    indexName
  } = useReportsQueriesSelector();

  const { errorModal } = useModals();

  const { pathname } = useLocation();

  const exportTableName: string = useMemo(() => {
    let name = "Export table";

    (Object.keys(exportTableNames) as RoutesKeys[]).forEach((routeKey) => {
      const nameFromCollection: string | undefined = exportTableNames[routeKey];
      if (matchPath(pathname, Routes[routeKey]) && nameFromCollection) {
        name = nameFromCollection;
      }
    });

    return name;
  }, [pathname]);

  const isPageWithExportFromServer: boolean = useMemo(() => {
    return routesForServerExports.some((route) => matchPath(pathname, route));
  }, [pathname]);

  const {
    getRawTableAction,
    getIrrCalculationsAction,
    exportMeasureTableAction,
    getOverviewTableAction,
    getBridgeDataAction
  } = useReportsActions();

  const downloadFile = ({ data, type = "text/csv", name = "data.csv" }: DownloadFile) => {
    const blob = new Blob([data], { type });
    const elem = document.createElement("a");
    elem.href = URL.createObjectURL(blob);
    elem.download = name;
    elem.click();
    URL.revokeObjectURL(elem.href);
  };

  const addRowsToCsv = (csv: string[][], rows: DealTableRow[], cols: string[]): void => {
    rows.forEach((row) => {
      const csvRow = cols.map((col) => row.valueData[col]?.toString() || "");
      csvRow.splice(0, 0, row.title);
      csv.push(csvRow);
      if (row.children) {
        addRowsToCsv(csv, row.children, cols);
      }
    });
  };
  const addColsToCSV = (csv: string[][], cols: { key: string; title: string }[]) => {
    const columns = cols.map((col) => col.title);
    columns.splice(0, 0, "Group/Value");
    calcFields.forEach((fieldMeta) => {
      if (!columns.includes(fieldMeta)) {
        columns.push(fieldMeta);
      }
    });
    csv.push(columns);
  };

  const exportMeasureTable = useCallback(async () => {
    try {
      setIsLoading(true);
      setAnchor(null);
      await exportMeasureTableAction({
        dateRange,
        secondaryDateRange,
        measures,
        groupBy,
        calculatedFields,
        rowBy,
        currency,
        originalCurrency,
        characteristics,
        dealTableRowBy,
        dealTableGroupBy,
        dealTableColumnBy,
        filters,
        exchangeMethod,
        use_accounting_date,
        columns: location.pathname === Routes.snapshot ? reportTableColumns : [],
        indexName: indexName
      });
    } catch (e) {
      errorModal({ msg: (e as IApiServiceError).message });
    } finally {
      setIsLoading(false);
    }
  }, [
    exportMeasureTableAction,
    dateRange,
    secondaryDateRange,
    measures,
    groupBy,
    calculatedFields,
    rowBy,
    currency,
    originalCurrency,
    characteristics,
    dealTableRowBy,
    dealTableGroupBy,
    dealTableColumnBy,
    filters,
    exchangeMethod,
    use_accounting_date,
    reportTableColumns,
    indexName
  ]);

  const handleExportToCsv = () => {
    if (isPageWithExportFromServer) {
      exportMeasureTable();
    } else {
      const csv: string[][] = [];

      if (tableData) {
        addColsToCSV(csv, tableData.columns);
        const colKeys = tableData.columns.map((col) => col.key);
        Object.keys(calcFields).forEach((field) => {
          if (!colKeys.includes(field)) {
            colKeys.push(field);
          }
        });
        addRowsToCsv(csv, tableData.rows, colKeys);
      }

      const csvString = csv.map((row) => row.join(",")).join("\r\n");
      downloadFile({ data: csvString });
      setAnchor(null);
    }
  };

  const handleExportRawData = async () => {
    try {
      setIsLoading(true);
      setAnchor(null);
      // const { data: table } = await getRawTableAction();
      await getRawTableAction({
        filters,
        dateRange,
        measures,
        characteristics,
        currency,
        exchangeMethod,
        calculatedFields,
        filteredPortfolio,
        use_accounting_date
      });
      // const csv: string[][] = [];
      // if (table) {
      //   csv.push(table.columns.map((col: any) => col.title));
      //   const colKeys = table.columns.map((col: any) => col.key);
      //   table.rows.forEach((row: Record<string, string>) => {
      //     csv.push(colKeys.map((key: string) => row[key]));
      //   });
      // }

      // const csvString = csv.map((row) => row.join(",")).join("\r\n");
      // downloadFile(table);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleBridgeData = async () => {
    try {
      setIsLoading(true);
      setAnchor(null);
      await getBridgeDataAction({
        filters,
        dateRange,
        currency,
        exchangeMethod,
        use_accounting_date
      });
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleIRRGraph = async () => {
    openIRRGraphModal(true);
  };

  const handleIrrCalculations = async () => {
    try {
      setIsLoading(true);
      setAnchor(null);
      await getIrrCalculationsAction({
        filters,
        dateRange,
        currency,
        exchangeMethod,
        use_accounting_date
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverviewTable = async () => {
    try {
      setIsLoading(true);
      setAnchor(null);
      await getOverviewTableAction({
        dateRange,
        measures,
        calculatedFields,
        currency,
        characteristics,
        exchangeMethod,
        filteredPortfolio,
        filters,
        use_accounting_date
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <TooltipDiv tooltipMessage="Export Data">
        <IconButton sx={{ margin: "-8px" }} onClick={(e: any) => !isLoading && setAnchor(e.target)}>
          {isLoading ? <CircularProgress size={25} /> : <MoreVertIcon sx={{ color: "#0a3142" }} />}
        </IconButton>
      </TooltipDiv>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={!!anchorEl}
        onClose={() => setAnchor(null)}
        TransitionComponent={Fade}
      >
        <MenuItem onClick={handleExportToCsv}>{startCase(exportTableName)}</MenuItem>
        <MenuItem onClick={handleExportRawData}>Export Raw Data</MenuItem>
        <MenuItem onClick={handleBridgeData}>Export Bridge Data</MenuItem>
        <MenuItem onClick={handleIRRGraph}>Show IRR Graph</MenuItem>
        {dealId && matchPath(pathname, Routes.dealReport) && (
          <MenuItem onClick={handleIrrCalculations}>Export IRR Calculation</MenuItem>
        )}
        {dealId && matchPath(pathname, Routes.dealReport) && (
          <>
            <MenuItem onClick={handleOverviewTable}>Export Overview Table</MenuItem>
            <NullifyUnfundedCommitment />
          </>
        )}
      </Menu>
    </>
  );
};
